<template>
    <div class="w-full h-full flex flex-col gap-2 text-xs">
        <!-- Top bar: filter + follow -->
        <div class="flex items-center gap-2 flex-shrink-0">
            <input type="text" v-model="messageFilter"
                class="flex-1 px-2 py-1 border border-white/20 rounded bg-white/15 text-white placeholder-slate-500 focus:outline-none focus:border-ui-active"
                placeholder="Filter topics" />
            <label class="flex items-center gap-1.5 text-gray-400 cursor-pointer select-none">
                <input type="checkbox" v-model="followMessages"
                    class="accent-cyan-500 cursor-pointer" />
                follow
            </label>
        </div>

        <!-- Main area: message table + controls -->
        <div class="flex flex-row gap-3 flex-1 overflow-hidden min-h-0">
            <!-- Message table -->
            <div ref="tableContainer" class="flex-1 overflow-auto bg-white/15 border border-white/20 rounded">
                <table class="w-full table-auto text-left border-collapse">
                    <colgroup>
                        <col style="width: 15%;">
                        <col style="width: 25%;">
                        <col style="width: 60%;">
                    </colgroup>
                    <thead>
                        <tr class="bg-ui-bg text-ui-active sticky top-0">
                            <th class="px-2 py-1.5 font-medium">Timestamp</th>
                            <th class="px-2 py-1.5 font-medium">Topic</th>
                            <th class="px-2 py-1.5 font-medium">Message</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="({ topic, timestamp, message }, index) in filteredMessages" :key="index"
                            class="border-t border-white/20 hover:bg-white/10">
                            <td class="px-2 py-1 whitespace-nowrap text-slate-400">{{ timestamp }}</td>
                            <td class="px-2 py-1 whitespace-nowrap text-ui-active font-mono cursor-pointer hover:text-ui-active"
                                @click="setMqttTopic(topic)">{{ topic }}</td>
                            <td class="px-2 py-1 whitespace-nowrap font-mono cursor-pointer"
                                :class="isErrorOrMqttMessage(topic) ? 'text-red-400' : 'text-green-400'"
                                @click="setMqttPayload(message)">
                                {{ message }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Controls sidebar -->
            <div class="w-48 flex-shrink-0 flex flex-col gap-1.5 bg-white/10 border border-white/20 rounded p-2">
                <div>
                    <label class="block text-gray-400 mb-0.5">Buffer</label>
                    <select v-model="maxLines"
                        class="w-full px-2 py-1 border border-white/20 rounded bg-white/15 text-white focus:outline-none focus:border-ui-active appearance-none">
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="200">200</option>
                        <option value="500">500</option>
                        <option value="1000">1000</option>
                        <option value="10000">10000</option>
                    </select>
                </div>
                <div>
                    <label class="block text-gray-400 mb-0.5">Command</label>
                    <select @change="plateConfig($event.target.value)"
                        class="w-full px-2 py-1 border border-white/20 rounded bg-white/15 text-white focus:outline-none focus:border-ui-active appearance-none">
                        <option value="wifi">wifi</option>
                        <option value="mqtt">mqtt</option>
                        <option value="mdns">mdns</option>
                        <option value="hasp">hasp</option>
                        <option value="gui">gui</option>
                        <option value="debug">debug</option>
                        <option value="gpio">gpio</option>
                    </select>
                </div>
                <hr class="border-white/20 my-0.5" />
                <form-button color="gray"    @click="clearMessages">clear</form-button>
                <form-button color="gray"    @click="mqttStatus">status</form-button>
                <form-button color="gray"    @click="mqttClearpage">clearpage</form-button>
                <form-button color="success" @click="mqttcalibrate">calibrate</form-button>
                <form-button color="danger"  @click="mqttReboot">reboot</form-button>
            </div>
        </div>

        <!-- MQTT command bar -->
        <div class="flex-shrink-0 flex flex-col gap-1">
            <span class="text-gray-400 font-medium">MQTT command</span>
            <div class="flex gap-2">
                <input type="text" v-model="mqttTopic"
                    class="w-2/5 px-2 py-1 border border-white/20 rounded bg-white/15 text-white placeholder-slate-500 font-mono focus:outline-none focus:border-ui-active"
                    placeholder="mqtt topic" />
                <input type="text" v-model="mqttPayload"
                    class="flex-1 px-2 py-1 border border-white/20 rounded bg-white/15 text-white placeholder-slate-500 font-mono focus:outline-none focus:border-ui-active"
                    placeholder="payload" />
                <form-button color="gray" @click="sendMqtt" icon="media/icons/send.png" class="w-16" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, watchEffect, onUnmounted, nextTick, computed } from 'vue';
import mqttService from '../../services/MqttService';
import { icon } from '@fortawesome/fontawesome-svg-core';

const props = defineProps({
    popupState: Boolean, // Detect when tab opens
    settings: {
        type: Object,
        required: true,
    },
});

const tableContainer = ref(null);
const messageFilter = ref('');
const followMessages = ref(true);
const receivedMessages = ref([]); // Array for incoming messages
const maxLines = ref(20); // Default max lines
const mqttTopic = ref('/command/statusupdate'); // MQTT command input
const mqttPayload = ref(''); // MQTT payload input

// Function to handle incoming messages
const receivedMessage = (timestamp, topic, message) => {
    // Push new message
    receivedMessages.value.push({ topic, timestamp, message });

    // Check if the number of messages exceeds maxLines, and remove oldest messages
    if (receivedMessages.value.length > maxLines.value) {
        const excess = receivedMessages.value.length - maxLines.value;
        receivedMessages.value.splice(0, excess); // Remove excess messages from the beginning of the array
    }

    // Auto-scroll if followMessages is true
    if (followMessages.value) {
        nextTick(() => {
            if (tableContainer.value) {
                tableContainer.value.scrollTop = tableContainer.value.scrollHeight;
            }
        });
    }
};

// Register MQTT listener when popup is opened
watchEffect(() => {
    if (props.popupState) {
        if (!mqttService.isConnected()) {
            mqttService.connectToBroker(props.settings);
        }
        mqttService.registerListener('mqtt_tab', receivedMessage);
    }
});

// Clean up when component is unmounted
onUnmounted(() => {
    mqttService.unregisterListener('mqtt_tab');
});

computed(() => {
});

const isErrorOrMqttMessage = (topic) => {
    return topic.toLowerCase().includes('error') || topic.toLowerCase().includes('mqtt');
};

const plateConfig = (item) => {
    mqttService.sendMqttCommand('/command/config/' + item);
};

const clearMessages = () => {
    receivedMessages.value = [];
};

const mqttStatus = () => {
    mqttService.sendMqttCommand('/command/statusupdate');
};

const mqttClearpage = () => {
    mqttService.sendMqttCommand('/command/clearpage', 'all');
};

const mqttcalibrate = () => {
    mqttService.sendMqttCommand('/command/calibrate');
};

const mqttReboot = () => {
    mqttService.sendMqttCommand('/command/reboot');
};

const sendMqtt = () => {
    mqttService.sendMqttCommand(mqttTopic.value, mqttPayload.value);
};

const setMqttTopic = (topic) => {
    const strippedTopic = topic.replace(mqttService.mqttTarget, ''); // Remove the mqttTarget prefix
    mqttTopic.value = strippedTopic;
};

const setMqttPayload = (message) => {
    mqttPayload.value = message;
};


// Computed property for filtered messages
const filteredMessages = computed(() => {
    if (!messageFilter.value) {
        return receivedMessages.value;
    }
    return receivedMessages.value.filter(({ topic }) =>
        topic.toLowerCase().includes(messageFilter.value.toLowerCase())
    );
});
</script>
